import {
  colorBaseBlueDark,
  colorBaseBlueLight,
  colorBaseBluePrimary,
  colorBaseGrayscaleLightGray,
  colorBaseGrayscaleOffBlack,
  colorBaseGrayscaleOffWhite,
  colorBaseGrayscaleShadeGray,
  colorBaseGreenDark,
  colorBaseGreenLight,
  colorBaseGreenPrimary,
  colorBaseOrangeDark,
  colorBaseOrangeLight,
  colorBasePurpleDark,
  colorBasePurpleLight,
  colorBasePurplePrimary,
  colorBaseRedDark,
  colorBaseRedLight,
  colorBaseSandDark,
  colorBaseSandLight,
  colorBaseSandPrimary,
  colorBaseTealDark,
  colorBaseTealLight,
  colorBaseTealPrimary,
  colorBaseWhiteDark,
  colorBaseWhitePrimary,
  colorPaletteBlue600,
  colorPaletteGrayscale500,
  colorPaletteGreen200,
  colorPaletteGreen600,
  colorPaletteGreen700,
  colorPaletteOrange600,
  colorPalettePurple600,
  colorPaletteRed300,
  colorPaletteRed600,
  colorSeverityFlagDark,
  colorSeverityFlagLight,
  colorSeverityFlagMain,
  colorSeverityLimitationDark,
  colorSeverityLimitationMain,
  colorSeverityRepairDark,
  colorSeverityRepairLight,
  colorSeverityRepairMain,
  colorSeveritySafetyDark,
  colorSeveritySafetyLight,
  colorSeveritySafetyMain,
} from '@inspectify/design';
import './fonts.css';
import { ThemeOptions } from '@mui/material';
import { alpha, getContrastRatio } from '@mui/material/styles';

/**
 * https://m2.material.io/inline-tools/color/
 * https://zenoo.github.io/mui-theme-creator/#text-buttons
 * https://mui.com/material-ui/customization/default-theme/
 * https://mui.com/material-ui/customization/default-theme/?expand-path=$.palette
 */
const theme = (mode: 'light' | 'dark'): ThemeOptions => ({
  palette: {
    mode,
    background: {
      default: mode === 'light' ? colorBaseGrayscaleOffWhite : colorBaseGrayscaleOffBlack,
    },
    primary: {
      light: colorBaseBlueLight,
      main: mode === 'light' ? colorPaletteBlue600 : colorBaseBlueLight,
      // dark: colorBaseBlueDark,
    },
    secondary: {
      light: colorBaseTealLight,
      main: mode === 'light' ? colorBaseTealDark : colorPaletteGreen200,
      // dark: colorBaseTealDark,
    },
    error: {
      // light: colorBaseRedLight,
      main: mode === 'light' ? colorPaletteRed600 : colorPaletteRed300,
      // dark: colorBaseRedDark,
    },
    warning: {
      light: colorBaseOrangeLight,
      main: colorPaletteOrange600,
      // dark: colorBaseOrangeDark,
    },
    info: {
      light: colorBaseBlueLight,
      main: colorPalettePurple600,
      // dark: colorBaseBlueDark,
    },
    success: {
      light: colorBaseGreenLight,
      main: colorPaletteGreen700,
      // dark: colorBaseGreenDark,
    },
    // Custom alternate page colors
    alternateBackground: {
      light: colorBaseBlueLight,
      main: mode === 'light' ? colorBaseBlueLight : colorBaseGrayscaleOffBlack,
      dark: colorBaseGrayscaleOffBlack,
      contrastText: 'fff'
    },
    // action: {
    //   active: mode === 'light' ? colorBaseGrayscaleOffWhite : colorBaseGrayscaleShadeGray,
    //   hover: mode === 'light' ? colorBaseGrayscaleOffWhite : colorBaseGrayscaleShadeGray,
    // },
    // custom colors based on object status
    // https://mui.com/material-ui/customization/theming/#customizing-colors
    requested: {
      light: colorBaseGreenLight,
      main: colorPaletteGreen700,
      dark: colorBaseGreenDark,
      contrastText: '#fff',
    },
    scheduled: {
      light: colorBasePurpleLight,
      main: colorBasePurplePrimary,
      dark: colorBasePurpleDark,
      contrastText: '#fff',
    },
    cancelled: {
      light: colorBaseOrangeLight,
      main: colorPaletteOrange600,
      dark: colorBaseOrangeDark,
      contrastText: '#fff',
    },
    completed: {
      light: colorBaseGreenLight,
      main: colorPaletteGreen700,
      dark: colorBaseGreenDark,
      contrastText: '#fff',
    },
    in_progress: {
      light: colorBaseBlueLight,
      main: colorBaseBluePrimary,
      dark: colorBaseBlueDark,
      contrastText: '#fff',
    },
    reschedule_requested: {
      light: colorBaseGreenLight,
      main: colorPaletteGreen700,
      dark: colorBaseGreenDark,
      contrastText: '#fff',
    },
    draft: {
      light: colorBaseTealLight,
      main: colorBaseTealPrimary,
      dark: colorBaseTealDark,
      contrastText: '#fff',
    },
    offered: {
      light: colorBasePurpleLight,
      main: colorBasePurplePrimary,
      dark: colorBasePurpleDark,
      contrastText: '#fff',
    },
    accepted: {
      light: colorBaseGreenLight,
      main: colorPaletteGreen700,
      dark: colorBaseGreenDark,
      contrastText: '#fff',
    },
    declined: {
      light: colorBaseRedLight,
      main: colorPaletteRed600,
      dark: colorBaseRedDark,
      contrastText: '#fff',
    },
    expired: {
      light: colorBaseOrangeLight,
      main: colorPaletteOrange600,
      dark: colorBaseOrangeDark,
      contrastText: '#fff',
    },
    pending: {
      light: colorBaseBlueLight,
      main: colorBaseBluePrimary,
      dark: colorBaseBlueDark,
      contrastText: '#fff',
    },
    unavailable: {
      light: colorBaseGrayscaleOffWhite,
      main: colorPaletteGrayscale500,
      dark: colorBaseGrayscaleShadeGray,
      contrastText: '#fff',
    },
    // Severity
    safety: {
      light: colorSeveritySafetyLight,
      main: colorSeveritySafetyMain,
      dark: colorSeveritySafetyDark,
      contrastText: '#fff',
    },
    repair: {
      light: colorSeverityRepairLight,
      main: colorSeverityRepairMain,
      dark: colorSeverityRepairDark,
      contrastText: '#fff',
    },
    monitor: {
      light: colorBaseSandLight,
      main: colorBaseSandPrimary,
      dark: colorBaseSandDark,
      contrastText: '#fff',
    },
    // Issue types
    inspectify_flag: {
      light: colorSeverityFlagLight,
      main: colorSeverityFlagMain,
      dark: colorSeverityFlagDark,
      contrastText: '#fff',
    },
    limitation: {
      light: '#fdfcc6',// todo phil: color not updating
      main: colorSeverityLimitationMain,
      dark: colorSeverityLimitationDark,
      contrastText: '#fff',
    },
    fixed: {
      light: colorPaletteGreen200,
      main: colorPaletteGreen600,
      dark: colorPaletteGreen700,
      contrastText: '#fff',
    },
    // ReportStatus
    not_started: {
      light: colorBaseRedLight,
      main: colorPaletteRed600,
      dark: colorBaseRedDark,
      contrastText: '#fff',
    },
    inspected: {
      light: colorBaseGreenLight,
      main: colorPaletteGreen700,
      dark: colorBaseGreenDark,
      contrastText: '#fff',
    },
    cannot_inspect: {
      light: colorBaseBlueLight,
      main: colorPalettePurple600,
      dark: colorBaseBlueDark,
      contrastText: '#fff',
    },
    not_present: {
      light: colorBaseGrayscaleLightGray,
      main: '#eeeeee',
      dark: colorBaseGrayscaleShadeGray,
      contrastText: colorBaseGrayscaleOffBlack,
    },
  },
  // TODO: review
  // colorSchemes: {
  //   dark: {
  //     primary: {
  //       main: colorPaletteBlue500,
  //     },
  //   },
  // },
  // https://mui.com/material-ui/react-typography/#roboto-font
  typography: {
    fontFamily: 'Nunito Sans',
    h1: {
      fontSize: 40,
      fontWeight: 700,
      lineHeight: 'normal',
    },
    h2: {
      fontSize: 36,
      fontWeight: 700,
      lineHeight: 'normal',
    },
    h3: {
      fontSize: 32,
      fontWeight: 700,
      lineHeight: 'normal',
    },
    h4: {
      fontSize: 24,
      // fontWeight: 400,
      lineHeight: 'normal',
    },
    h5: {
      fontSize: 20,
      // fontWeight: 400,
      // lineHeight: 'normal',
    },
    h6: {
      fontSize: 18,
      // fontWeight: 400,
      lineHeight: 'normal',
    },
    // p1
    body1: {
      fontSize: 16,
      fontWeight: 400,
      lineHeight: 'normal',
    },
    // p2
    body2: {
      fontSize: 14,
      fontWeight: 400,
      // lineHeight: '34px',
      // letterSpacing: '0.25px',
    },
    // p3
    subtitle1: {
      fontSize: 16,
      fontWeight: 400,
      lineHeight: '28px',
      letterSpacing: '0.15px',
    },
    // p4
    subtitle2: {
      fontSize: 12,
      fontWeight: 400,
      // lineHeight: 'normal',
      // letterSpacing: '0.25px',
    },
    button: {
      fontWeight: 700,
      fontSize: 16,
    },
    caption: {
      fontSize: 12,
    },
    overline: {
      fontSize: 14,
    },
  },
  shape: {
    borderRadius: 4,
  },
  components: {
    // https://mui.com/material-ui/api/app-bar/#classes
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiCard: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiChip: {
      defaultProps: {
        variant: 'filled',
      },
      styleOverrides: {
        root: (theme) => {
          // @ts-ignore
          const paletteElement = theme.theme.palette[theme.ownerState.color] || theme.theme.palette.action;
          return ({
            variants: [
              {
                props: { variant: 'twoTone' },
                style: {
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: paletteElement?.main || theme.ownerState.color,
                  backgroundColor: paletteElement?.light || theme.ownerState.color,
                  // color: getContrastRatio(alpha(paletteElement?.light || '#fff', 0.7), '#fff') > 4.5 ? '#fff' : '#111',
                  color: paletteElement?.dark,
                  '.MuiChip-icon': {
                    color: paletteElement?.dark || theme.ownerState.color,
                  }
                },
              },
              {
                props: { variant: 'twoTone', color: 'error' },
                style: {
                  backgroundColor: colorBaseRedLight,
                },
              }, {
                props: { variant: 'twoTone', color: 'inspectify_flag' },
                style: {
                  backgroundColor: colorBaseGrayscaleOffWhite,
                  borderColor: colorBaseGrayscaleLightGray,
                  color: getContrastRatio(alpha(paletteElement?.light || '#fff', 0.7), '#fff') > 4.5 ? '#fff' : '#111',
                  '.MuiChip-icon': {
                    // @ts-ignore
                    color: theme.theme.palette['inspectify_flag'].main,
                  }
                },
              },
            ],
          });
        },
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          '&:-webkit-autofill': {
            'WebkitBoxShadow':
              mode === 'light'
                ? '0 0 0 100px var(--inspectify-palette-pending-light) inset'
                : '0 0 0 100px var(--inspectify-palette-pending-dark) inset',
            'WebkitTextFillColor': 'var(--inspectify-palette-text-primary)',
          },
        },
      },
    },
    // @ts-ignore
    MuiDataGrid: {
      styleOverrides: {
        root: {
          '& .MuiDataGrid-columnHeaderTitle': {
            fontSize: 12,
            fontWeight: 700,
            variant: 'caption',
            wordWrap: 'break-word',
            whiteSpace: 'normal',
            lineHeight: 'normal',
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          color: colorBaseWhitePrimary, // thumb (OFF)
          "&.Mui-checked": {
            color: mode === 'light' ? colorPaletteBlue600 : colorBaseTealDark, // thumb (ON)
            "& + .MuiSwitch-track": {
              backgroundColor: colorBaseWhiteDark, // ON track
              opacity: 1,
            },
          },
        },
        track: {
          backgroundColor: colorBaseWhiteDark, // OFF track
          opacity: 1,
        },
      },
    },
    // MuiButton: {
    //   styleOverrides: {}
    // }
  },
});

export default theme;

